import React, { FC, Suspense } from 'react';
import { every, filter, find, map, orderBy, some } from 'lodash';
import {
  BlockStatusCode,
  MilestoneStatusCode,
  CommodityStatusCode,
  ItrType,
  CommodityMilestoneActivity,
  MilestoneActivity,
} from '@bechtel/types';
import { Box, useSideSheetState, Icon, Text } from '@bechtel/gds';
import { MilestoneAction } from './action';
import { MilestoneMetadata, AssociatedWeldsMetadata } from './metadata';
import { MilestoneHours } from './hours';
import { MilestoneAttachmentAction } from './attachment';
import { MilestoneListProps, MilestoneProps } from './types';
import {
  MilestoneCardStyled,
  MilestoneTitleStyled,
  MilestoneBodyStyled,
  CommodityMilestoneListStyled,
  MilestoneIndicatorStyled,
  MilestoneAttachmentContainerStyled,
} from './styled';
import { ErrorBoundary, SuspenseLoader, useConfig, useAppUser } from '@bechtel/client-common';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { MilestoneActionButtonStyled } from './action/styled';
import { useWorkPackage } from '~/detail/context';
import WeldFlangeConnections from './weld-flange-connections';
import { Trans } from '@lingui/macro';
import WeldDetails from './weld-details/internals';

export const Milestone: FC<MilestoneProps> = ({ hidden, milestone, isUserClaimer }) => {
  const { features } = useConfig();
  const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });
  const status = hasOpenBlocks ? MilestoneStatusCode.BLOCKED : milestone.currentStatus.codeI18n;
  const { assignedCrewMembers } = useWorkPackage();
  const { canApproveMilestone, canRejectMilestone } = useWorkPackagePermissions(assignedCrewMembers);
  const hasAttachments = !!milestone.attachments.length;
  const { visible: weldDetailVisible, open: weldDetailOpen, close: weldDetailClose } = useSideSheetState(false);
  const weldIds = map(milestone.weldStatuses, 'id');
  const hasAssociatedWelds = milestone.weldStatuses && milestone.weldStatuses.length > 0;
  const isFullCardLinkRequired = !!(hasAssociatedWelds && canApproveMilestone && canRejectMilestone);
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const itrRequired = some(milestone.type.projectItrConfig, (item) => {
    return item.itrType === ItrType.PIPE_CLEANLINESS;
  });

  const showViewDetailLabel =
    isFitupEnabled &&
    hasAssociatedWelds &&
    every(milestone.weldStatuses, (weld) => weld?.status?.status == CommodityStatusCode.VT_APPROVED);
  return (
    <MilestoneCardStyled status={status}>
      <MilestoneIndicatorStyled status={status} />
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} hidden={hidden}>
        <MilestoneTitleStyled status={status}>
          {milestone.name}
          {hasAttachments && canApproveMilestone && canRejectMilestone && (
            <MilestoneAttachmentContainerStyled>
              <MilestoneAttachmentAction milestone={milestone} status={status} isInverted={true} />
            </MilestoneAttachmentContainerStyled>
          )}
        </MilestoneTitleStyled>
        <MilestoneBodyStyled
          isFullCardLinkRequired={isFullCardLinkRequired}
          onClick={() => {
            isFullCardLinkRequired && weldDetailOpen();
          }}
        >
          <MilestoneHours status={status} hours={milestone.hours} />
          <MilestoneMetadata status={status} milestone={milestone} />
          {hasAssociatedWelds && (
            <Box pt={{ sm: '300' }} flexDirection={{ sm: 'column' }}>
              <AssociatedWeldsMetadata weldStatuses={milestone.weldStatuses}></AssociatedWeldsMetadata>
            </Box>
          )}
        </MilestoneBodyStyled>
        <Box
          pb={{ sm: '400' }}
          mt={{ sm: '100' }}
          px={{ sm: '400' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'flex-end' }} //Justify-content to flex end  to show weld details/attachment button at the leftmost end if milestone action section is not visible
        >
          <MilestoneAction status={status} milestone={milestone} isUserClaimer={isUserClaimer} />
          {hasAssociatedWelds &&
            (status === MilestoneStatusCode.CLAIMED ? !canApproveMilestone && !canRejectMilestone : true) && (
              <Box onClick={weldDetailOpen} flexGrow={{ sm: 1 }} ml={{ sm: `${showViewDetailLabel ? '300' : '000'}` }}>
                <MilestoneActionButtonStyled status={status} data-testid="weld-details-btn">
                  <Icon icon="list"></Icon>
                  {!showViewDetailLabel && (
                    <Box ml={{ sm: '400' }}>
                      <Text color="grey.1000" size="300">
                        <Trans>View Details</Trans>
                      </Text>
                    </Box>
                  )}
                </MilestoneActionButtonStyled>
              </Box>
            )}
          {weldDetailVisible && weldIds.length && (
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              {isFitupEnabled ? (
                <WeldFlangeConnections close={weldDetailClose} weldIds={weldIds} itrRequired={itrRequired} />
              ) : (
                <WeldDetails close={weldDetailClose} weldList={milestone.weldStatuses} />
              )}
            </Suspense>
          )}
          {hasAttachments && !canApproveMilestone && !canRejectMilestone && (
            <MilestoneAttachmentAction milestone={milestone} status={status} />
          )}
        </Box>
      </Box>
    </MilestoneCardStyled>
  );
};

export const MilestoneList: FC<MilestoneListProps> = ({ hidden, milestones, activityHistory }) => {
  const { user } = useAppUser();
  return (
    <CommodityMilestoneListStyled milestonesCount={milestones.length}>
      {milestones.map((milestone) => {
        const milestoneActivityHistory = orderBy(
          filter(activityHistory, (item: CommodityMilestoneActivity) => {
            return item.milestoneName === milestone.name;
          }),
          'dateTime',
          'desc'
        );
        const latestClaimActivity = find(
          milestoneActivityHistory,
          (item) => item.activity === MilestoneActivity.CLAIMED
        );
        const isUserClaimer = latestClaimActivity ? latestClaimActivity.crewMember?.id === user?.id : false;
        return (
          <ErrorBoundary key={milestone.name}>
            <Milestone milestone={milestone} hidden={hidden} isUserClaimer={isUserClaimer} />
          </ErrorBoundary>
        );
      })}
    </CommodityMilestoneListStyled>
  );
};
